/**
 * Basic Life Benefit Definitions
 *
 * Uses builder functions for standard type/amount pairs.
 * Enum values from @platform/openapi-types passed via typeEnumValues or options.
 */

import {
  BaseBasicLifeAdndBenefitBenefitAmountType,
  BaseBasicLifeAdndBenefitBenefitMaximumType,
  BaseBasicLifeAdndBenefitGuaranteeIssueType,
  BaseBenefitPlanPolicyPeriod,
  DefinitionOfEarningsEnum,
  EmployerContributionEnum,
  extendEnumWithNotDisclosed,
  NotDisclosed,
  RateGuaranteeTypeEnum,
  RfpBasicLifeAdndBenefitSpouseBenefitType,
  RfpInclusionStatusEnum,
  RfpPortabilityType,
} from "@platform/openapi-types";

import { mpOnlyRow } from "../shared/definitions/mpOnlyRow";
import { BenefitDefinitions } from "../../benefits/benefit.types";
import {
  createEnumFormatter,
  createValueFormatter,
} from "../../components/PlanEditor/utilities/enumFormatters";
import * as sharedCellSpecs from "../shared/cellSpecs/common";
import {
  definitionOfEarningsLabels,
  DOLLAR_AMOUNT_LABEL,
  employerContributionLabels,
  SALARY_MULTIPLIER_LABEL,
} from "../shared/definitions";
import {
  buildAgeReductionScheduleBenefitPair,
  buildCommissionBenefits,
  buildTypeAmountBenefitPair,
  buildTypeMultipleAmountsBenefit,
} from "../shared/definitions/builders";

import { EARNINGS_TYPES_WITHOUT_AVERAGE_MONTHS } from "./constants";

// Pre-build commission definitions to override isRequired on type fields
const commissionDefs = buildCommissionBenefits();

export const benefits: BenefitDefinitions = {
  currentCarrierName: {
    label: "Current Carrier",
    description: "Name of the current insurance carrier",
    cell: sharedCellSpecs.currentCarrierName,
    benefitPlanLevel: true,
    isRequired: true,
    editableWhen: {
      dependsOn: "policyPeriod",
      disabledValues: [
        BaseBenefitPlanPolicyPeriod.newBusiness,
        BaseBenefitPlanPolicyPeriod.alternate,
      ],
    },
  },
  ...commissionDefs,
  currentCommissionType: {
    ...commissionDefs["currentCommissionType"],
    isRequired: true,
    editableWhen: {
      dependsOn: "policyPeriod",
      disabledValues: [
        BaseBenefitPlanPolicyPeriod.newBusiness,
        BaseBenefitPlanPolicyPeriod.alternate,
      ],
    },
  },
  requestedCommissionType: {
    ...commissionDefs["requestedCommissionType"],
    isRequired: true,
  },
  eligibilityClasses: {
    label: "Eligibility Class",
    description: "Benefit classes assigned to this benefit plan group",
    cell: sharedCellSpecs.eligibilityClasses,
    benefitPlanLevel: true,
    isRequired: true,
  },
  ...buildTypeMultipleAmountsBenefit({
    prefix: "benefitAmount",
    typeLabel: "Benefit Amount",
    typeCell: sharedCellSpecs.enumDropdown,
    typeEnumValues: extendEnumWithNotDisclosed(
      BaseBasicLifeAdndBenefitBenefitAmountType,
    ),
    typeDescription: "Describes the benefit configuration available",
    amountFields: [
      {
        suffix: "DollarAmount",
        label: DOLLAR_AMOUNT_LABEL,
        cell: sharedCellSpecs.dollarAmount,
        description: "Benefit amount in dollars",
        defaultValue: 1,
        disabledValues: [
          BaseBasicLifeAdndBenefitBenefitAmountType.salaryMultiplier,
          NotDisclosed,
        ],
      },
      {
        suffix: "SalaryMultiplier",
        label: SALARY_MULTIPLIER_LABEL,
        cell: sharedCellSpecs.salaryMultiplier,
        description: "Benefit amount as salary multiplier",
        defaultValue: 1,
        disabledValues: [
          BaseBasicLifeAdndBenefitBenefitAmountType.dollarAmount,
          BaseBasicLifeAdndBenefitBenefitAmountType.increments,
          NotDisclosed,
        ],
      },
    ],
    disabledValues: ["notIncluded", "notApplicable"],
    isRequired: true,
  }),
  ...buildTypeMultipleAmountsBenefit({
    prefix: "benefitMaximum",
    mpConfigKey: "maximum_amount",
    typeLabel: "Maximum Amount",
    typeCell: sharedCellSpecs.enumDropdown,
    typeEnumValues: extendEnumWithNotDisclosed(
      BaseBasicLifeAdndBenefitBenefitMaximumType,
    ),
    typeDescription:
      "Indicates the maximum amount of benefit the employee can elect",
    amountFields: [
      {
        suffix: "DollarAmount",
        label: DOLLAR_AMOUNT_LABEL,
        cell: sharedCellSpecs.dollarAmount,
        description: "Maximum benefit dollar amount",
        defaultValue: 1,
        disabledValues: [
          BaseBasicLifeAdndBenefitBenefitMaximumType.salaryMultiplier,
          NotDisclosed,
        ],
      },
      {
        suffix: "SalaryMultiplier",
        label: SALARY_MULTIPLIER_LABEL,
        cell: sharedCellSpecs.salaryMultiplier,
        description: "Maximum benefit salary multiplier",
        defaultValue: 1,
        disabledValues: [
          BaseBasicLifeAdndBenefitBenefitMaximumType.dollarAmount,
          NotDisclosed,
        ],
      },
    ],
    isRequired: true,
  }),
  ...buildTypeMultipleAmountsBenefit({
    prefix: "guaranteeIssue",
    typeLabel: "Guarantee Issue",
    typeCell: sharedCellSpecs.enumDropdown,
    typeEnumValues: extendEnumWithNotDisclosed(
      BaseBasicLifeAdndBenefitGuaranteeIssueType,
    ),
    typeDescription:
      "Indicates the amount of Life Insurance volume available without having to provide Evidence of Insurability",
    amountFields: [
      {
        suffix: "SalaryMultiplier",
        label: SALARY_MULTIPLIER_LABEL,
        cell: sharedCellSpecs.salaryMultiplier,
        description: "Guarantee issue salary multiplier",
        defaultValue: 1,
        disabledValues: [
          BaseBasicLifeAdndBenefitGuaranteeIssueType.allGuaranteeIssue,
          BaseBasicLifeAdndBenefitGuaranteeIssueType.dollarAmount,
          BaseBasicLifeAdndBenefitGuaranteeIssueType.notIncluded,
          NotDisclosed,
        ],
      },
      {
        suffix: "DollarAmount",
        label: DOLLAR_AMOUNT_LABEL,
        cell: sharedCellSpecs.dollarAmount,
        description: "Guarantee issue dollar amount",
        defaultValue: 1,
        disabledValues: [
          BaseBasicLifeAdndBenefitGuaranteeIssueType.allGuaranteeIssue,
          BaseBasicLifeAdndBenefitGuaranteeIssueType.notIncluded,
          NotDisclosed,
        ],
      },
      {
        suffix: "AdditionalAmount",
        label: "Additional Amount",
        cell: sharedCellSpecs.dollarAmount,
        description: "Guarantee issue additional amount",
        defaultValue: 1,
        disabledValues: [
          BaseBasicLifeAdndBenefitGuaranteeIssueType.allGuaranteeIssue,
          BaseBasicLifeAdndBenefitGuaranteeIssueType.dollarAmount,
          BaseBasicLifeAdndBenefitGuaranteeIssueType.salaryMultiplierWithCap,
          BaseBasicLifeAdndBenefitGuaranteeIssueType.notIncluded,
          NotDisclosed,
        ],
      },
    ],
    disabledValues: ["notIncluded", "notApplicable"],
    isRequired: true,
  }),
  dependentBenefitsIncluded: {
    label: "Dependent Benefits Included",
    description: "Whether dependent (spouse/child) benefits are included",
    cell: sharedCellSpecs.benefitsIncluded,
    networkDependent: false,
    refreshesCells: ["spouseBenefitType", "childBenefitType"],
  },
  ...buildTypeAmountBenefitPair({
    prefix: "spouseBenefit",
    typeLabel: "Benefit Amount - Spouse",
    typeCell: sharedCellSpecs.enumDropdown,
    typeEnumValues: extendEnumWithNotDisclosed(
      RfpBasicLifeAdndBenefitSpouseBenefitType,
    ),
    amountCell: sharedCellSpecs.dollarAmount,
    typeDescription: "Indicates the amount of benefit available for a spouse",
    amountDescription: "Spouse benefit amount in dollars",
    amountDefaultValue: 1,
    typeEditableWhen: {
      dependsOn: "dependentBenefitsIncluded",
      disabledValues: ["no"],
      resetValue: "notIncluded",
    },
  }),
  ...buildTypeAmountBenefitPair({
    prefix: "childBenefit",
    typeLabel: "Benefit Amount - Child",
    typeCell: sharedCellSpecs.enumDropdown,
    typeEnumValues: extendEnumWithNotDisclosed(
      RfpBasicLifeAdndBenefitSpouseBenefitType,
    ),
    amountCell: sharedCellSpecs.dollarAmount,
    typeDescription: "Indicates the amount of benefit available for a child",
    amountDescription: "Child benefit amount in dollars",
    amountDefaultValue: 1,
    typeEditableWhen: {
      dependsOn: "dependentBenefitsIncluded",
      disabledValues: ["no"],
      resetValue: "notIncluded",
    },
  }),
  ...buildAgeReductionScheduleBenefitPair({
    isRequired: true,
  }),
  ...buildTypeAmountBenefitPair({
    prefix: "definitionOfEarnings",
    mpConfigKey: "definition_of_earnings",
    typeLabel: "Definition of Earnings",
    typeCell: sharedCellSpecs.enumDropdown,
    typeEnumValues: extendEnumWithNotDisclosed(DefinitionOfEarningsEnum),
    typeCustomLabels: definitionOfEarningsLabels,
    amountLabel: "Average Months",
    amountCell: sharedCellSpecs.numberField,
    typeDescription: "Indicates the earnings covered by the plan",
    amountDescription: "The time period covered when considering earnings",
    // Blank, not 1: average months is optional, and 0 is a real value.
    amountDefaultValue: null,
    valueSuffix: "AverageMonths",
    disabledValues: EARNINGS_TYPES_WITHOUT_AVERAGE_MONTHS,
  }),
  portability: {
    label: "Portability (with or without EOI)",
    description:
      "Indicates if policy holder can continue policy after separation of employment and whether Evidence of Insurability would be required",
    networkDependent: false,
    cell: {
      ...sharedCellSpecs.enumDropdown,
      cellEditorParams: {
        ...sharedCellSpecs.enumDropdown.cellEditorParams,
        values: extendEnumWithNotDisclosed(RfpPortabilityType),
      },
    },
  },
  conversion: {
    label: "Conversion",
    mpConfigKey: "conversion",
    description:
      "Indicates if the employee will be allowed to convert the basic life policy to a different form of life policy",
    networkDependent: false,
    cell: {
      ...sharedCellSpecs.enumDropdown,
      cellEditorParams: {
        ...sharedCellSpecs.enumDropdown.cellEditorParams,
        values: extendEnumWithNotDisclosed(RfpInclusionStatusEnum),
      },
    },
  },
  acceleratedDeathBenefit: {
    label: "Accelerated Death Benefit",
    mpConfigKey: "accelerated_death_benefit",
    description:
      "Indicates if the plan will allow for any amount of life insurance benefit to be paid out in advance of death",
    networkDependent: false,
    cell: {
      ...sharedCellSpecs.enumDropdown,
      cellEditorParams: {
        ...sharedCellSpecs.enumDropdown.cellEditorParams,
        values: extendEnumWithNotDisclosed(RfpInclusionStatusEnum),
      },
    },
  },
  waiverOfPremium: {
    label: "Waiver of Premium",
    mpConfigKey: "waiver_of_premium",
    description:
      "Indicates if premium payments will be waived in the case of a critical illness or serious injury",
    networkDependent: false,
    cell: {
      ...sharedCellSpecs.enumDropdown,
      cellEditorParams: {
        ...sharedCellSpecs.enumDropdown.cellEditorParams,
        values: extendEnumWithNotDisclosed(RfpInclusionStatusEnum),
      },
    },
  },
  employerContribution: {
    label: "Employer Contribution",
    description: "Defines employer vs employee contribution structure",
    cell: {
      ...sharedCellSpecs.employerContribution,
      cellEditorParams: {
        ...sharedCellSpecs.employerContribution.cellEditorParams,
        values: extendEnumWithNotDisclosed(EmployerContributionEnum),
        formatValue: createEnumFormatter(employerContributionLabels),
      },
      valueFormatter: createValueFormatter(employerContributionLabels),
    },
    networkDependent: false,
    isRequired: true,
  },
  ...buildTypeAmountBenefitPair({
    prefix: "rateGuarantee",
    typeLabel: "Rate Guarantee",
    typeCell: sharedCellSpecs.enumDropdown,
    typeEnumValues: Object.keys(RateGuaranteeTypeEnum),
    amountCell: sharedCellSpecs.numberField,
    typeDescription:
      "Time frame in which rates are guaranteed to stay the same",
    amountDescription: "Number of months or years the rate is guaranteed",
    amountDefaultValue: 1,
    disabledValues: [
      RateGuaranteeTypeEnum.notIncluded,
      RateGuaranteeTypeEnum.notOffered,
    ],
  }),
  enrolledLives: mpOnlyRow("Enrolled Lives", "enrolled_lives"),

  // Extended (non-v5) fields (CD-1662/CD-2070). Backend fields are plain
  // strings for now (RFC-004), not yet enums, so all of these are text
  // rather than dropdowns, except eligibilityHours (CD-2325).
  eligibilityHours: {
    mpOnly: true,
    label: "Eligibility Hours",
    mpConfigKey: "eligibility_hours",
    description: "Minimum hours worked per week to be eligible for the plan",
    cell: sharedCellSpecs.eligibilityHoursDropdown,
    networkDependent: false,
  },
  waiverOfPremiumEligibility: {
    mpOnly: true,
    label: "Waiver of Premium - Eligibility",
    mpConfigKey: "waiver_of_premium_eligibility",
    description: "Eligibility criteria for the waiver of premium benefit",
    // An age of 0 isn't a real eligibility age, so this is a positive
    // integer (min 1), not the generic numberField's min 0.
    cell: {
      ...sharedCellSpecs.numberField,
      prefix: "Age",
      cellEditorParams: {
        ...sharedCellSpecs.numberField.cellEditorParams,
        min: 1,
      },
      validation: { ...sharedCellSpecs.numberField.validation, minimum: 1 },
    },
    networkDependent: false,
  },
  waiverOfPremiumWaitingPeriod: {
    mpOnly: true,
    label: "Waiver of Premium Waiting Period",
    mpConfigKey: "waiver_of_premium_waiting_period",
    description: "Waiting period before the waiver of premium benefit applies",
    cell: { ...sharedCellSpecs.numberField, suffix: "months" },
    networkDependent: false,
  },
  waiverOfPremiumDuration: {
    mpOnly: true,
    label: "Waiver of Premium - Duration",
    mpConfigKey: "waiver_of_premium_duration",
    description: "Duration for which the waiver of premium benefit applies",
    // An age of 0 isn't a real duration age, so this is a positive integer
    // (min 1), not the generic numberField's min 0.
    cell: {
      ...sharedCellSpecs.numberField,
      prefix: "Age",
      cellEditorParams: {
        ...sharedCellSpecs.numberField.cellEditorParams,
        min: 1,
      },
      validation: { ...sharedCellSpecs.numberField.validation, minimum: 1 },
    },
    networkDependent: false,
  },
  airBag: {
    mpOnly: true,
    label: "Air Bag",
    mpConfigKey: "air_bag",
    description: "Air bag benefit details for AD&D coverage",
    cell: sharedCellSpecs.includedNotIncludedDropdown,
    networkDependent: false,
  },
  commonCarrier: {
    mpOnly: true,
    label: "Common Carrier",
    mpConfigKey: "common_carrier",
    description: "Common carrier benefit details for AD&D coverage",
    cell: sharedCellSpecs.includedNotIncludedDropdown,
    networkDependent: false,
  },
  repatriation: {
    mpOnly: true,
    label: "Repatriation",
    mpConfigKey: "repatriation",
    description: "Repatriation benefit details for AD&D coverage",
    cell: sharedCellSpecs.includedNotIncludedDropdown,
    networkDependent: false,
  },
  seatBelt: {
    mpOnly: true,
    label: "Seat Belt",
    mpConfigKey: "seat_belt",
    description: "Seat belt benefit details for AD&D coverage",
    cell: sharedCellSpecs.includedNotIncludedDropdown,
    networkDependent: false,
  },
  eapFaceToFaceVisitsLimitType: {
    mpOnly: true,
    label: "EAP - Face to Face Visits Limit Type",
    mpConfigKey: "eap_number_of_face_to_face_visits",
    description: "Type of limit placed on EAP face-to-face visits",
    cell: sharedCellSpecs.eapFaceToFaceVisitsLimitTypeDropdown,
    networkDependent: false,
  },
  eapVisitCount: {
    mpOnly: true,
    label: "EAP - Visit Count",
    mpConfigKey: "eap_number_of_face_to_face_visits",
    description: "Number of EAP visits allowed under the plan",
    cell: sharedCellSpecs.text,
    networkDependent: false,
  },
  travelAid: {
    mpOnly: true,
    label: "Travel Aid",
    mpConfigKey: "travel_aid",
    description: "Travel aid benefit details for AD&D coverage",
    cell: sharedCellSpecs.includedNotIncludedDropdown,
    networkDependent: false,
  },
} as const;
